import React, { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/atoms/button/button'
import { t } from '../../utils/i18n'
import Advertiser from '../../components/organisms/advertiser/advertiser'
import CustomModal from '../../components/molecules/modal/modal'
import CustomUploader, {
  CustomUploaderRef,
  FileType,
} from '../../components/molecules/custom-file-uploader/custom-file-uploader'
import axios from '../../utils/axios'
import { useAuth } from '../../customHooks/useAuth'
import { downloadFile } from '../../utils/helper-functions'
import { UPLOAD_DOC_URL, DOC_TEMPLATE_URL, PARSE_DOC_URL } from '../../constants/routes'
import { showToast } from '../../components/atoms/toaster/toaster'
import { InfoTooltip } from '../../components/atoms/info-tooltip/info-tooltip'
import { CustomDropdown, DropdownOption } from '../../components/molecules/dropdown/dropdown'
import { ADVERTISER_LISTS } from '../../constants/routes'
import { ReactComponent as Info } from '../../assets/svgs/Info.svg'
import { useNavigate } from 'react-router-dom'

const ManageAdvertiser = () => {
  const { user } = useAuth()
  const [isModalOpen, isSetModalOpen] = useState(false)
  const [isLoading, isSetLoading] = useState(false)
  const uploaderRef = useRef<CustomUploaderRef>(null)
  const [files, setFiles] = useState<FileType[]>([])
  const [advertiserList, setAdvertiserList] = useState([{ id: 'all', name: 'All' }])
  const [selectedAdvertiser, setSetSelectedAdvertiser] = useState<string | null>(null)
  const [sortLatest, setSortLatest] = useState('')
  const [shouldPageRefresh, setShouldPageRefresh] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    if (shouldPageRefresh) {
      getAdvertiserLists()
      setShouldPageRefresh(false)
    }
  }, [shouldPageRefresh])

  const getAdvertiserLists = async () => {
    const config = {
      method: 'GET',
      url: ADVERTISER_LISTS,
      headers: {
        'x-user': user?.email,
        'x-access-token': user?.tokens?.accessToken?.token,
        'x-source': user?.isSSO ? 'sso' : null,
      },
    }
    try {
      const dropDownOptions = await axios(config)
      const newAdvertisers = dropDownOptions?.data?.data ?? []
      setAdvertiserList([...[advertiserList[0]], ...newAdvertisers])
    } catch {
      showToast({ message: t('fetch.advertisers.failure'), isError: true })
    }
  }
  const handleAdvertiserAddition = async () => {
    await uploaderRef.current?.handleManualUpload()
  }

  const getNonBoardedIds = (ids: string[]) => {
    let str = ''
    ids.forEach((id) => {
      str += `${str !== '' ? ',' : ' '} ${id}`
    })
    return str
  }

  const handleDownTemplate = async () => {
    const config = {
      method: 'GET',
      url: `${DOC_TEMPLATE_URL}`,
      headers: {
        'x-user': user?.email,
        'x-access-token': user?.tokens?.accessToken?.token,
        'x-source': user?.isSSO ? 'sso' : null,
      },
    }
    try {
      const getResponse = await axios(config)
      const fileUrl = getResponse?.data?.data?.url

      if (fileUrl) {
        downloadFile(fileUrl)
      }
    } catch {
      showToast({ message: t('download.file.template.failure'), isError: true })
    }
  }
  // eslint-disable-next-line
  const handleDocParse = async (res: any) => {
    try {
      const config = {
        method: 'GET',
        url: `${PARSE_DOC_URL}?file=${res?.fileName}`,
        headers: {
          'x-user': user?.email,
          'x-access-token': user?.tokens?.accessToken?.token,
          'x-source': user?.isSSO ? 'sso' : null,
        },
      }

      const response = await axios(config)
      if (response?.data?.data?.status.toLowerCase() === 'success') {
        isSetModalOpen(false)
        setShouldPageRefresh(true)
        setSortLatest('latest')
        if (response?.data?.data?.code === 'INVALID_BUSINESSACCOUNTS_FOUND') {
          const businessAccountsOnboarded =
            response?.data?.data?.businessAccountsProvided -
            response?.data?.data?.businessAccountsNotOnboarded

          showToast({
            message: t(`${response?.data?.data?.code}`)
              .replace(
                '{{businessAccountsProvided}}',
                response?.data?.data?.businessAccountsProvided
              )
              .replace('{{businessAccountsOnboarded}}', businessAccountsOnboarded),
            description: t('INVALID_BUSINESSACCOUNTS_FOUND.desc').replace(
              '{{businessManagerNotBoarded}}',
              getNonBoardedIds(response.data?.data?.businessIdsNotOnboarded)
            ),
            isWarning: true,
          })
          setSortLatest('latest')
        } else if (response?.data?.data?.code === 'ONBOARDING_SUCCESSFUL') {
          showToast({ message: t(`${response?.data?.data?.code}`) })
        }
      }
      // eslint-disable-next-line
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        isSetModalOpen(false)
        showToast({
          message: t('upload.failed.title'),
          description: error?.response?.data.data.code
            ? t(`${error?.response?.data.data.code}`)
            : t('upload.generic.error'),
          isError: true,
          duration: 6000,
        })
      }
    }
    isSetLoading(false)
    setFiles([])
  }

  const handleSelectedAdvertiser = async (option: DropdownOption) => {
    setSetSelectedAdvertiser(option?.name)
  }
  return (
    <>
      <div className="info-advertiser">
        <Info />
        <div className="info-text-container">
          <p className="info-heading">{t('manage.advertiser.info.heading')}</p>
          <p>
            <span className="info-text">{t('manage.advertiser.info.text')}</span>
            <span
              className="info-text--bold"
              onClick={() => navigate('/business-accounts?status=UNMAPPED')}
            >
              {t('manage.advertiser.info.subText')}
            </span>
          </p>
        </div>
      </div>
      <div className="dashboard">
        <div className="link-account-container dashboard-inner-container advertiser-container">
          <div className="top-section">
            <div className="dropdown-wrapper">
              <div className="title-info">
                <p className="label-input">{t('advertiser.name')}</p>
                <InfoTooltip
                  styleClass="input-info"
                  tooltipContent={t('manage.advertiser.dropdown.tooltip')}
                  placement={'right'}
                />
              </div>
              <div className="dropdown">
                <CustomDropdown
                  id="advertiser-selector"
                  className="label-input"
                  label=""
                  placeholder={t('advertiser.dropdown.placeholder')}
                  options={advertiserList}
                  onSelect={(option) => {
                    handleSelectedAdvertiser(option)
                  }}
                />
              </div>
            </div>
            <Button
              label={t('add.advertiser.acc')}
              handleClick={() => isSetModalOpen(!isModalOpen)}
            />
            <CustomModal
              customModalWrapperName="advertiser-modal"
              isOpen={isModalOpen}
              size="29.5rem"
              title={t('add.ad.acc')}
              continueTitle={t('upload.cta')}
              isSetModalOpen={isSetModalOpen}
              isFormIncomplete={files.length === 0}
              handleSubmit={handleAdvertiserAddition}
              isLoading={isLoading}
              isAbortCTARequired={false}
              body={
                <section>
                  <ul className="upload-notes">
                    {t('upload.notes')
                      .split(',')
                      .map((a: string, i: number) => (
                        <li key={i}>{a}</li>
                      ))}
                  </ul>
                  <CustomUploader
                    ref={uploaderRef}
                    apiEndpoint={`${UPLOAD_DOC_URL}`}
                    accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    height={80}
                    message={
                      <span className="label-input">
                        {t('upload.file.title')}
                        <span>{t('upload.file.subtitle')}</span>
                      </span>
                    }
                    onSuccess={(res) => {
                      handleDocParse(res)
                    }}
                    onError={() => {
                      showToast({ message: t('upload.generic.error'), isError: true })
                    }}
                    onChange={(files) => setFiles(files)}
                    draggable={true}
                  />
                  <Button
                    buttonWithText
                    handleClick={handleDownTemplate}
                    label={t('download.file.template.title')}
                    className="label-input"
                  />
                </section>
              }
            />
          </div>
          <div className="bottom-section">
            <Advertiser
              isSetModalOpen={isSetModalOpen}
              isBMOnboarded={false}
              selectedAdvertiser={selectedAdvertiser}
              sortLatest={sortLatest}
              setSortLatest={setSortLatest}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default ManageAdvertiser
